import { TOGGLE_LINK_COMMAND } from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ButtonGroup, Glyphicon, Overlay } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import PencilSquareIcon from 'assets/images/icons/pencil-square.svg?react';
import Button from 'components/button/Button';
import { RichTextEditorEditLinkPopover } from '../RichTextEditor.styles';
import { EditLinkPluginModalFormValues } from './editLink/EditLinkPluginModal.schema';
import useEditLinkPluginModal from './editLink/useEditLinkPluginModal';
import useEditLinkPluginSelection from './editLink/useEditLinkPluginSelection';

interface EditLinkPluginProps {
  container: HTMLElement | null;
}

const EditLinkPlugin = ({ container }: EditLinkPluginProps) => {
  const { t } = useTranslation();
  const [editor] = useLexicalComposerContext();
  const { isEditLinkModalOpen, openEditLinkModal } = useEditLinkPluginModal();
  const { handleLinkSubmit, isLink, linkUrl, selectedLinkProps, targetRef } =
    useEditLinkPluginSelection();

  const handleOnSubmit = (values: EditLinkPluginModalFormValues) => {
    handleLinkSubmit(values);
  };

  const handleEditClick = () => {
    openEditLinkModal({
      defaultValues: {
        openInNewWindow: selectedLinkProps?.target === '_blank',
        text: selectedLinkProps?.text ?? '',
        url: linkUrl ?? '',
      },
      onSubmit: handleOnSubmit,
    });
  };

  const handleRemoveLink = () => {
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
  };

  const popover = (
    <RichTextEditorEditLinkPopover id="edit-link-popover" placement="bottom">
      <a
        href={linkUrl}
        target="_blank"
        rel="noopener noreferrer"
        title={linkUrl}
      >
        {linkUrl}
      </a>

      <ButtonGroup>
        <Button
          bsStyle="default"
          data-testid="edit-link-plugin-edit-button"
          onClick={handleEditClick}
        >
          <PencilSquareIcon aria-label={t('common.edit')} />
        </Button>
        <Button
          bsStyle="default"
          data-testid="edit-link-plugin-remove-button"
          onClick={handleRemoveLink}
        >
          <Glyphicon aria-label={t('common.delete')} glyph="trash" />
        </Button>
      </ButtonGroup>
    </RichTextEditorEditLinkPopover>
  );

  if (!isLink || !targetRef.current) {
    return null;
  }

  return (
    <Overlay
      show={isLink && !isEditLinkModalOpen}
      target={targetRef.current}
      placement="bottom"
      container={container}
      containerPadding={20}
      rootClose
      shouldUpdatePosition
    >
      {popover}
    </Overlay>
  );
};

export default EditLinkPlugin;
